<template>
  <div class="list-wrapper inline-block w-64 h-full align-top whitespace-nowrap mx-1">
    <div
      class="board-list rounded bg-gray-200 flex flex-col max-h-full relative whitespace-normal border-t-4"
      :style="{ borderColor: pipelineStage.color }"
    >
      <div
        :class="['list-header', 'pl-4', 'pr-2', 'py-3', 'flex', 'justify-between', 'items-center']"
      >
        <div class="text-sm font-medium truncate pr-2">
          {{ pipelineStage.name }}
        </div>
        <div class="flex space-x-2 items-center">
          <span
            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
          >
            {{ totalContactRequests }}
          </span>
          <InfoModal v-if="pipelineStage.description" :content="pipelineStage.description" html />
          <PipelineStageActions :pipeline="pipeline" :pipeline-stage="pipelineStage" />
        </div>
      </div>
      <div
        :class="[
          'list-cards',
          'flex-auto',
          'overflow-y-auto',
          'overflow-x-hidden',
          'mx-1',
          'px-1',
          '-mt-2',
          'min-h-0',
        ]"
      >
        <Draggable
          :list="contactRequests"
          group="pipelineStage"
          draggable=".draggable"
          handle=".handle"
          @change="switchPipelineStage"
        >
          <template v-if="hasContactRequests">
            <div
              v-for="contactRequest in contactRequests"
              :key="contactRequest.id"
              :class="[
                'draggable',
                'block',
                'text-sm',
                'focus:outline-none',
                'rounded',
                'shadow',
                'my-2',
                ...(lastFocusedContactRequest === contactRequest.id
                  ? ['text-white', 'bg-indigo-500', 'hover:bg-indigo-600']
                  : ['text-gray-900', 'bg-white', 'hover:bg-gray-200']),
              ]"
              @click="handleContactRequestClick(contactRequest)"
            >
              <div class="card-details p-2 flex">
                <div class="flex-shrink-0 pr-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="w-4 h-4 handle cursor-move text-gray-400 hover:text-black"
                  >
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <circle cx="9" cy="5" r="1" />
                    <circle cx="9" cy="12" r="1" />
                    <circle cx="9" cy="19" r="1" />
                    <circle cx="15" cy="5" r="1" />
                    <circle cx="15" cy="12" r="1" />
                    <circle cx="15" cy="19" r="1" />
                  </svg>
                </div>
                <div class="flex-1">
                  {{ contactRequest.fullName }}
                </div>
              </div>
            </div>
          </template>
        </Draggable>
        <div v-if="loading" class="text-center p-2">
          <Loader small />
        </div>

        <div v-else-if="!hasContactRequests" class="p-4 italic text-center text-sm my-2">
          No Contact Requests
        </div>

        <div v-else-if="hasMore" class="p-2 italic text-center text-sm my-2">
          <BaseButton type="button" primary class="w-full" @click="fetchNextPage">
            Load more
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable';
import InfoModal from 'Components/InfoModal.vue';
import PipelineStageActions from './PipelineStageActions.vue';

export default {
  components: { Draggable, InfoModal, PipelineStageActions },
  props: {
    pipeline: {
      type: Object,
      required: true,
    },
    pipelineStage: {
      type: Object,
      required: true,
    },
    lastFocusedContactRequest: {
      type: String,
      default: null,
    },
    assignedToMe: Boolean,
    projectId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      meta: null,
      loading: false,
      description: false,
    };
  },

  computed: {
    contactRequests() {
      return this.$store.getters['ui/pipelineStageContactRequests/itemsByParent'](
        this.pipelineStage.id
      ).filter((contactRequest) =>
        this.assignedToMe ? contactRequest.userId === this.$user.id : true
      );
    },
    hasContactRequests() {
      return this.contactRequests.length > 0;
    },
    hasMore() {
      const { current_page: currentPage = 1, last_page: lastPage = 1 } = this.meta || {};
      return currentPage < lastPage;
    },
    totalContactRequests() {
      const contactRequestsCount = this.contactRequests.length;
      const { to = 0, total = 0 } = this.meta || {};

      return contactRequestsCount - to + total;
    },
  },

  created() {
    this.fetchContactRequests();
  },

  destroyed() {
    this.$store.commit('ui/pipelineStageContactRequests/setIdsByParent', {
      parentId: this.pipelineStage.id,
      ids: [],
    });
  },

  methods: {
    setMeta(meta) {
      this.meta = meta;
    },
    setLoading(value) {
      this.loading = Boolean(value);
    },
    async fetchContactRequests(page = 1) {
      try {
        this.setLoading(true);
        const { meta } = await this.$store.dispatch('ui/pipelineStageContactRequests/load', {
          id: this.pipelineStage.id,
          query: {
            ...(this.projectId ? { filter: { project_id_is: this.projectId } } : {}),
            sort: 'created_at',
            page_size: 800,
            page,
          },
          append: page > 1,
        });

        this.setMeta(meta);
      } catch (error) {
        //
      } finally {
        this.setLoading(false);
      }
    },
    fetchNextPage() {
      if (!this.hasMore) {
        return;
      }

      this.fetchContactRequests(this.meta.current_page + 1);
    },
    async switchPipelineStage(evt) {
      if (typeof evt.added === 'undefined') {
        return;
      }

      const { element } = evt.added;

      try {
        await this.$store.dispatch('ui/pipelineStageContactRequests/switch', {
          contactRequestId: element.id,
          to: this.pipelineStage.id,
          from: element.pipelineStageId,
        });
      } catch (error) {
        //
      }
    },
    handleContactRequestClick(contactRequest) {
      this.$emit('edit', contactRequest);
    },
  },
};
</script>
